import React, { useState, useEffect } from 'react';
import { getAllActivityLogs } from '../../services/activityLogService';
import Timeline from '../common/Timeline';
import Pagination from '../common/Pagination';
import SearchInput from '../common/SearchInput';
import { Calendar, Download, Loader } from 'lucide-react';
import { format } from 'date-fns';

const LogsTab = ({ users }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    // Estado para os filtros que estão sendo digitados
    const [filters, setFilters] = useState({ searchQuery: '', startDate: '', endDate: '' });
    // Estado para os filtros que foram submetidos e estão ativos
    const [activeFilters, setActiveFilters] = useState({ searchQuery: '', startDate: '', endDate: '' });

    useEffect(() => {
        const fetchLogs = async () => {
            setLoading(true);
            setError(null);
            try {
                const { logs: fetchedLogs, total } = await getAllActivityLogs(currentPage, activeFilters.searchQuery, activeFilters.startDate, activeFilters.endDate);
                setLogs(fetchedLogs);
                setTotalPages(Math.ceil(total / 15));
            } catch (err) {
                setError('Falha ao carregar os registros de atividade.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchLogs();
    }, [currentPage, activeFilters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        setActiveFilters(filters);
    };

    const handleExportCsv = async () => {
        try {
            const { logs: allLogs } = await getAllActivityLogs(1, activeFilters.searchQuery, activeFilters.startDate, activeFilters.endDate, true);

            if (allLogs.length === 0) {
                alert("Nenhum dado para exportar.");
                return;
            }

            const headers = ["ID do Log", "Email do Usuário", "Tipo de Atividade", "Alvo", "ID do Alvo", "Detalhes", "Timestamp"];
            const csvRows = [headers.join(',')];

            allLogs.forEach(log => {
                const row = [
                    log.id,
                    `"${log.userEmail}"`,
                    `"${log.activityType}"`,
                    `"${log.target}"`,
                    `"${log.targetId}"`,
                    `"${log.details || ''}"`,
                    `"${format(new Date(log.timestamp), "yyyy-MM-dd HH:mm:ss")}"`
                ];
                csvRows.push(row.join(','));
            });

            const csvString = csvRows.join('\n');
            const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `registros_de_atividade_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (err) {
            console.error("Erro ao exportar para CSV:", err);
            alert("Falha ao exportar os dados.");
        }
    };

    const handleAvatarClick = (user) => {
        console.log("Avatar clicado:", user);
    };

    return (
        <div className="p-4">
            {/* Filtros */}
            <form onSubmit={handleSearchSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <SearchInput
                    name="searchQuery"
                    placeholder="Pesquisar por email..."
                    value={filters.searchQuery}
                    onChange={handleFilterChange}
                    loading={loading}
                />
                <div className="relative">
                    <input
                        type="date"
                        name="startDate"
                        value={filters.startDate}
                        onChange={handleFilterChange}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
                <div className="relative">
                    <input
                        type="date"
                        name="endDate"
                        value={filters.endDate}
                        onChange={handleFilterChange}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
                <div className="flex items-center space-x-2">
                    <button type="submit" className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2">
                        Filtrar
                    </button>
                    <button onClick={handleExportCsv} type="button" aria-label="Exportar para CSV" title="Exportar para CSV" className="bg-green-600 text-white p-2 rounded-lg hover:bg-green-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2">
                        <Download className="h-5 w-5" />
                    </button>
                </div>
            </form>

            {/* Timeline de Atividades */}
            <div className="mt-6">
                {loading ? (
                    <div className="flex justify-center items-center h-64"><Loader className="animate-spin h-12 w-12 text-blue-500" /></div>
                ) : error ? (
                    <p className="text-center text-red-500">{error}</p>
                ) : logs.length > 0 ? (
                    <>
                        <Timeline activities={logs} users={users} onUserAvatarClick={handleAvatarClick} />
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={(page) => setCurrentPage(page)}
                        />
                    </>
                ) : (
                    <p className="text-center text-gray-500 dark:text-gray-400 py-10">Nenhum registro encontrado para os filtros selecionados.</p>
                )}
            </div>
        </div>
    );
};

export default LogsTab;